import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  BaseEdge,
  ControlButton,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  ReactFlowProvider,
  getSmoothStepPath,
  useReactFlow,
  type Edge,
  type EdgeProps,
  type EdgeTypes,
  type Node,
  type NodeProps,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Icon } from '@iconify/react';
import { useTheme } from '@circuitry-ai/doc-ui';
import { useTranslation } from 'react-i18next';
import type { NodeData, NodeKind, ToolInfo, Workflow } from './agentBuilderApi';
import { layoutFlow, NO_AT, YES_AT, type Placement, type Point, type Size } from './layoutFlow';

type FlowNode = Node<NodeData, NodeKind>;
type FlowEdge = Edge<{ waypoints: Point[]; loop: boolean }, 'routed'>;

interface FlowInfo {
  tools: Record<string, ToolInfo>;
  issues: Record<string, string[]>;
  steps: Record<string, number>;
  fresh: Record<string, 'added' | 'changed'>;
}
const FlowContext = createContext<FlowInfo>({ tools: {}, issues: {}, steps: {}, fresh: {} });

const KIND: Record<NodeKind, { label: string; cls: string }> = {
  start: { label: 'Start', cls: 'rounded-full' },
  action: { label: 'Tool call', cls: 'rounded-lg border-l-4 border-l-success' },
  ai: { label: 'AI step', cls: 'rounded-lg border-l-4 border-l-secondary' },
  decision: { label: 'Decision', cls: 'rounded-lg border-dashed border-primary' },
  handoff: { label: 'Hand to a person', cls: 'rounded-lg border-warning' },
  end: { label: 'End', cls: 'rounded-full' },
};

function Shell({ id, kind, selected, children }: { id: string; kind: NodeKind; selected?: boolean; children: React.ReactNode }) {
  const { t } = useTranslation();
  const { issues, steps, fresh } = useContext(FlowContext);
  const problems = issues[id] ?? [];
  const isFresh = fresh[id];
  const ring = problems.length
    ? 'border-2 border-danger'
    : isFresh === 'added'
      ? 'ring-4 ring-success/50'
      : isFresh === 'changed'
        ? 'ring-4 ring-warning/50'
        : '';
  return (
    <div
      className={`relative w-[230px] ${kind === 'decision' ? 'pr-9' : ''} border border-default-300 bg-content1 text-foreground px-3 py-2 text-xs shadow-sm ${KIND[kind].cls} ${ring} ${selected ? 'outline outline-2 outline-primary' : ''}`}
      title={problems.join('\n') || undefined}
      data-testid={`ab-node-${id}`}
    >
      <div className="flex justify-between text-[10px] uppercase tracking-wide text-default-500">
        <span>
          {id in steps && <b className="text-primary mr-1">{t('Step')} {steps[id]}</b>}
          {t(KIND[kind].label)}
        </span>
        {isFresh && <span className={isFresh === 'added' ? 'text-success' : 'text-warning'}>{isFresh === 'added' ? t('new') : t('changed')}</span>}
      </div>
      {children}
      {problems.length > 0 && <div className="mt-1 text-danger">{problems[0]}</div>}
    </div>
  );
}

function StartNode({ id, data, selected }: NodeProps<FlowNode>) {
  return (
    <Shell id={id} kind="start" selected={selected}>
      <div className="font-semibold text-sm">{data.label || 'Start'}</div>
      {!!data.inputs?.length && <div className="text-default-500">Inputs: {data.inputs.join(', ')}</div>}
      <Handle type="source" position={Position.Right} />
    </Shell>
  );
}

function ActionNode({ id, data, selected }: NodeProps<FlowNode>) {
  const { tools } = useContext(FlowContext);
  const tool = data.tool ? tools[data.tool] : undefined;
  return (
    <Shell id={id} kind="action" selected={selected}>
      <Handle type="target" position={Position.Left} />
      <div className="font-semibold text-sm">{data.label || tool?.display_name}</div>
      <div className="text-default-500">
        {data.tool}
        {tool?.server ? ` · ${tool.server} MCP` : ''}
      </div>
      <Handle type="source" position={Position.Right} />
    </Shell>
  );
}

function AiNode({ id, data, selected }: NodeProps<FlowNode>) {
  return (
    <Shell id={id} kind="ai" selected={selected}>
      <Handle type="target" position={Position.Left} />
      <div className="font-semibold text-sm">{data.label || 'AI step'}</div>
      {data.note && <div className="text-default-500 line-clamp-2">{data.note}</div>}
      <Handle type="source" position={Position.Right} />
    </Shell>
  );
}

function DecisionNode({ id, data, selected }: NodeProps<FlowNode>) {
  return (
    <Shell id={id} kind="decision" selected={selected}>
      <Handle type="target" position={Position.Left} />
      <div className="font-semibold text-sm">{data.label || 'Decision'}</div>
      {data.condition && <div className="text-default-500 line-clamp-3">{data.condition}</div>}
      {/* Yes and No leave from the right edge, Yes above No, each labelled beside its handle */}
      <span className="absolute right-2 -translate-y-1/2 text-[10px] font-semibold text-success" style={{ top: `${YES_AT * 100}%` }}>
        Yes
      </span>
      <span className="absolute right-2 -translate-y-1/2 text-[10px] font-semibold text-danger" style={{ top: `${NO_AT * 100}%` }}>
        No
      </span>
      <Handle type="source" id="yes" position={Position.Right} style={{ top: `${YES_AT * 100}%` }} className="!bg-success" />
      <Handle type="source" id="no" position={Position.Right} style={{ top: `${NO_AT * 100}%` }} className="!bg-danger" />
    </Shell>
  );
}

function HandoffNode({ id, data, selected }: NodeProps<FlowNode>) {
  return (
    <Shell id={id} kind="handoff" selected={selected}>
      <Handle type="target" position={Position.Left} />
      <div className="font-semibold text-sm">{data.label || 'Hand to a person'}</div>
      <div className="text-default-500">Reason: {data.reason || '—'}</div>
      <Handle type="source" position={Position.Right} />
    </Shell>
  );
}

function EndNode({ id, data, selected }: NodeProps<FlowNode>) {
  return (
    <Shell id={id} kind="end" selected={selected}>
      <Handle type="target" position={Position.Left} />
      <div className="font-semibold text-sm">{data.label || 'Done'}</div>
      <div className="text-default-500">Outcome: {data.outcome || '—'}</div>
    </Shell>
  );
}

const nodeTypes: NodeTypes = {
  start: StartNode,
  action: ActionNode,
  ai: AiNode,
  decision: DecisionNode,
  handoff: HandoffNode,
  end: EndNode,
};

// An edge left to right through its lanes (see layoutFlow); its Yes/No is written on the decision
// card. An edge that loops back left is a plain step edge.
function RoutedEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, label, data, markerEnd, style }: EdgeProps<FlowEdge>) {
  if (data?.loop) {
    const [path, labelX, labelY] = getSmoothStepPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, borderRadius: 10, offset: 30 });
    return <BaseEdge id={id} path={path} label={label} labelX={labelX} labelY={labelY} markerEnd={markerEnd} style={style} />;
  }
  let path = `M ${sourceX} ${sourceY}`;
  let prev = { x: sourceX, y: sourceY };
  for (const p of [...(data?.waypoints ?? []), { x: targetX, y: targetY }]) {
    const mid = (prev.x + p.x) / 2;
    path += p.y === prev.y ? ` L ${p.x} ${p.y}` : ` C ${mid} ${prev.y} ${mid} ${p.y} ${p.x} ${p.y}`;
    prev = p;
  }
  return <BaseEdge id={id} path={path} markerEnd={markerEnd} style={style} />;
}

const edgeTypes: EdgeTypes = { routed: RoutedEdge };

// A long left-to-right workflow is far wider than the canvas: allow zooming out far enough that the
// whole of it always fits (React Flow stops at 0.5 by default); the user zooms in on the part they read.
const MIN_ZOOM = 0.05;
const MAX_ZOOM = 2;
const FIT = { padding: 0.05, minZoom: MIN_ZOOM, maxZoom: 1 };

function FlowInner({ workflow, selected, onSelect }: { workflow: Workflow; selected: string | null; onSelect: (id: string | null) => void }) {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { fitView } = useReactFlow();
  const { issues } = useContext(FlowContext);
  const box = useRef<HTMLDivElement | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  // Fit the view only when the workflow's shape changes, never on an unrelated re-render, so the
  // zoom the user chose stays put.
  const shape = useMemo(() => workflow.nodes.map((n) => `${n.id}@${n.position.x},${n.position.y}`).join('|'), [workflow]);
  // The cards are drawn hidden first, measured, then laid out from their real sizes (layoutFlow);
  // anything that can change a card's size lays it out again.
  const layoutKey = useMemo(
    () => JSON.stringify([workflow.nodes.map((n) => [n.id, n.data]), workflow.edges.map((e) => [e.source, e.target, e.sourceHandle]), issues]),
    [workflow, issues],
  );
  const [placed, setPlaced] = useState<(Placement & { key: string }) | null>(null);
  const ready = placed?.key === layoutKey;
  useEffect(() => {
    if (ready) return;
    // The cards' own sizes, read from the page (offset sizes ignore the canvas zoom). React Flow's
    // measurements are not used: its nodes here are controlled, so they never report as measured.
    let frame = 0;
    let tries = 0;
    const measure = () => {
      const sizes: Record<string, Size> = {};
      for (const n of workflow.nodes) {
        const card = box.current?.querySelector<HTMLElement>(`[data-testid="ab-node-${CSS.escape(n.id)}"]`);
        if (card?.offsetWidth) sizes[n.id] = { width: card.offsetWidth, height: card.offsetHeight };
      }
      // Not every card drawn yet: try again next frame (for about a second, then use estimates).
      if (Object.keys(sizes).length < workflow.nodes.length && tries++ < 60) {
        frame = requestAnimationFrame(measure);
        return;
      }
      setPlaced({ key: layoutKey, ...layoutFlow(workflow, sizes) });
    };
    frame = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(frame);
  }, [ready, layoutKey, workflow]);
  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === box.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void box.current?.requestFullscreen();
  };
  const nodes = useMemo<FlowNode[]>(
    () =>
      workflow.nodes.map((n) => ({
        id: n.id,
        type: n.type,
        position: (ready && placed.positions[n.id]) || n.position,
        data: n.data,
        selected: n.id === selected,
        draggable: false,
        style: ready ? undefined : { visibility: 'hidden' as const },
      })),
    [workflow, selected, ready, placed],
  );
  const edges = useMemo<FlowEdge[]>(
    () =>
      ready
        ? workflow.edges.map((e) => ({
            id: e.id,
            source: e.source,
            target: e.target,
            sourceHandle: e.sourceHandle ?? undefined,
            type: 'routed' as const,
            data: { waypoints: placed.waypoints[e.id] ?? [], loop: placed.loops.includes(e.id) },
            label: e.sourceHandle === 'yes' ? 'Yes' : e.sourceHandle === 'no' ? 'No' : undefined,
            markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
          }))
        : [],
    [workflow, ready, placed],
  );
  // Fit once a new shape has been laid out; a relayout of the same shape keeps the user's zoom.
  const fitted = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || fitted.current === shape) return;
    fitted.current = shape;
    const id = setTimeout(() => fitView({ ...FIT, duration: 300 }), 30);
    return () => clearTimeout(id);
  }, [ready, shape, fitView]);
  // The whole workflow stays in view when the canvas changes size (window, chat panel, full screen).
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    let id: ReturnType<typeof setTimeout> | undefined;
    const observer = new ResizeObserver(() => {
      clearTimeout(id);
      id = setTimeout(() => fitView({ ...FIT, duration: 200 }), 120);
    });
    observer.observe(el);
    return () => {
      clearTimeout(id);
      observer.disconnect();
    };
  }, [fitView]);
  return (
    <div ref={box} className="h-full w-full bg-background">
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      nodesDraggable={false}
      nodesConnectable={false}
      onNodeClick={(_, n) => onSelect(n.id)}
      onPaneClick={() => onSelect(null)}
      colorMode={isDark ? 'dark' : 'light'}
      fitView
      fitViewOptions={FIT}
      minZoom={MIN_ZOOM}
      maxZoom={MAX_ZOOM}
    >
      <Background />
      <Controls showInteractive={false} fitViewOptions={{ ...FIT, duration: 300 }}>
        <ControlButton onClick={toggleFullscreen} title={fullscreen ? t('Exit full screen') : t('Full screen')} aria-label={fullscreen ? t('Exit full screen') : t('Full screen')}>
          <Icon icon={fullscreen ? 'lucide:minimize-2' : 'lucide:maximize-2'} />
        </ControlButton>
      </Controls>
    </ReactFlow>
    </div>
  );
}

// The generated workflow, read-only: laid out here from the cards' sizes, with step numbers matching
// the agent's instructions, and the latest chat answer's new/changed steps highlighted.
export function BuilderFlow(props: {
  workflow: Workflow;
  tools: ToolInfo[];
  issues: Record<string, string[]>;
  steps: Record<string, number>;
  fresh: Record<string, 'added' | 'changed'>;
  selected: string | null;
  onSelect: (id: string | null) => void;
}) {
  const info = useMemo(
    () => ({
      tools: Object.fromEntries(props.tools.map((t) => [t.name, t])),
      issues: props.issues,
      steps: props.steps,
      fresh: props.fresh,
    }),
    [props.tools, props.issues, props.steps, props.fresh],
  );
  return (
    <FlowContext.Provider value={info}>
      <ReactFlowProvider>
        <FlowInner workflow={props.workflow} selected={props.selected} onSelect={props.onSelect} />
      </ReactFlowProvider>
    </FlowContext.Provider>
  );
}
